/**
 * Full-screen flow to pick a GuardRails-backed course type, enter titles, and create course + unit/lesson/screen.
 * @module Workspace/components/GuardRailsSelectorModal
 */
import { FullScreenDialogWrapper } from "../../../builder/Course/components/CourseContentPage/styles";
import { FullScreenDialog, MaterialIcon, Spinner } from "@pearsontechnology/aero-components";
import React, { useState } from "react";
import {
  CourseSelectionButton,
  OthersSubheader,
  TextWrapSubheader,
} from "../GuardRailsSelectorModal/styles";
import styled from "styled-components";
import {
  GUARDRAILS_COURSE_TYPES,
  GuardrailPluginMap,
} from "services/GuardRailsService/GuardRailsCourseTypes";
import GuardRailsService from 'services/GuardRailsService';
import PropTypes from "prop-types";
import pluginTypes from "../../../constants/pluginTypes";
import { dispatch as storeDispatch } from 'pluginServices/store';
import { pushToast } from 'containers/ToastManager/actions';
import { intl } from 'containers/LanguageProvider/IntlGlobalProvider';
import FloatingTextField from "./components/FloatingTextField";
import messages from './messages';
import {
  CreateCourseForm,
  HeaderWithBack,
  BackLink,
  CreateCourseTitle,
  FormRowWithButton,
  FormRowWithButtonInput,
  CreateButton,
} from "Workspace/components/GuardRailsSelectorModal/styles";
import { FullHeightWrapperWithBackGroundColor } from "../GuardRailsSelectorModal/components/fullHeightWrapper";

const CreateCourseButtonRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

/**
 * @param {object} props
 * @param {string} props.projectId - Project to attach the new course activity to.
 * @param {() => void} [props.onClose] - Closes the dialog after success or from chrome.
 * @param {() => void} [props.onSelectOther] - User chose a non–GuardRails course path.
 * @param {(pluginType: string, activityId: string) => void} [props.goToActivity] - Navigate to the new course.
 */
function GuardRailsSelectorModal({ projectId, onClose, onSelectOther, goToActivity }) {
  const selectCourserType = [
    intl.formatMessage(messages.bronteViewerEText),
    intl.formatMessage(messages.epubEText),
    intl.formatMessage(messages.other),
  ];
  const othersSubtitle = intl.formatMessage(messages.othersSubtitle);

  const [showCourseSelectionModal, setShowCourseSelectionModal] = useState(true);
  const [showCourseTitleModal, setShowCourseTitleModal] = useState(false);
  const [selectedCourseType, setSelectedCourseType] = useState('');
  const [selectedCoursePlugin, setSelectedCoursePlugin] = useState('');

  const [courseTitles, setCourseTitles] = useState({
    courseName: '',
    screenName: '',
    lessonName: '',
    unitName: '',
  });

  const [selectedScreenType, setSelectedScreenType] = useState('');

  const [errors, setErrors] = useState({});
  const [isCreatingCourse, setIsCreatingCourse] = useState(false);

  const isFormValid = Object.values(courseTitles).every((v) => v.trim());

  const onSelectCourse = (selectEvent) => {
    const { currentTarget } = selectEvent;
    const courseType = currentTarget.value;
    setSelectedCoursePlugin(GuardrailPluginMap[courseType]);
    setSelectedCourseType(courseType);
    setShowCourseSelectionModal(false);
    setShowCourseTitleModal(true);

    const plugins = GuardRailsService.getCourseScreenPlugins(courseType) || [];
    if (plugins.length > 0) {
      setSelectedScreenType(plugins[0].name);
    }
  };


  const updateTitle = (evn) => {
    const { value, name } = evn.currentTarget;
    setCourseTitles((prev) => ({ ...prev, [name]: value }));
  };
  
  const handleBack = () => {
    setShowCourseTitleModal(false);
    setShowCourseSelectionModal(true);
    setCourseTitles({
      courseName: '',
      screenName: '',
      lessonName: '',
      unitName: '',
    });
  };

  const onCreateCourse = async (e) => {
    e.preventDefault();
    if (isCreatingCourse) return;

    const newErrors = {};
    if (!courseTitles.courseName.trim()) newErrors.courseName = true;
    if (!courseTitles.screenName.trim()) newErrors.screenName = true;
    if (!courseTitles.lessonName.trim()) newErrors.lessonName = true;
    if (!courseTitles.unitName.trim()) newErrors.unitName = true;
    setErrors(newErrors);

    if (Object.keys(newErrors).length > 0) return;

    setIsCreatingCourse(true);
    try {
      if (courseTitles.courseName) {
        const titlesWithScreenType = { ...courseTitles, screenType: selectedScreenType };
        const { pathwayId, activityId } = await GuardRailsService.createCourseWithGuardRails(projectId, selectedCoursePlugin.name,
          selectedCourseType, courseTitles.courseName);

        await GuardRailsService.createUnitAndLessons(pathwayId, titlesWithScreenType, pluginTypes.UNIT, selectedCourseType);
        await goToActivity(pluginTypes.COURSE, activityId);
        storeDispatch(pushToast({ message: intl.formatMessage(messages.courseCreatedSuccess, { courseName: courseTitles.courseName }) }));
      }
      if (typeof onClose === 'function') {
        onClose();
      }
    } finally {
      setIsCreatingCourse(false);
    }
  };

  return (
    <>
      {
        showCourseSelectionModal && (<FullScreenDialogWrapper>
          <FullScreenDialog onClose={onClose}>
            <FullHeightWrapperWithBackGroundColor>
              <HeaderWithBack>
                <TextWrapSubheader>
                  {intl.formatMessage(messages.chooseCourseType)}
                </TextWrapSubheader>
              </HeaderWithBack>

              <CourseSelectionButton onClick={onSelectCourse} value={GUARDRAILS_COURSE_TYPES.BV}>
                {selectCourserType[0]}
              </CourseSelectionButton>
              <CourseSelectionButton onClick={onSelectCourse} value={GUARDRAILS_COURSE_TYPES.EPUB}>
                {selectCourserType[1]}
              </CourseSelectionButton>
              <CourseSelectionButton onClick={onSelectOther} value={GUARDRAILS_COURSE_TYPES.NONE}>
                {selectCourserType[2]}
                <OthersSubheader>{othersSubtitle}</OthersSubheader>
              </CourseSelectionButton>
            </FullHeightWrapperWithBackGroundColor>
          </FullScreenDialog>
        </FullScreenDialogWrapper>)
      }
      {
        showCourseTitleModal && (
          <FullScreenDialogWrapper>
            <FullScreenDialog onClose={onClose}>
              <FullHeightWrapperWithBackGroundColor>

                <CreateCourseForm>
                  <HeaderWithBack>
                    <BackLink
                      type="button"
                      onClick={handleBack}
                    >
                      <span> <MaterialIcon id="chevron_left" style={{ width: 24, height: 24, fontSize: 24 }} /> </span>
                      <CreateCourseTitle>
                        {intl.formatMessage(messages.createCourse)}
                      </CreateCourseTitle>
                    </BackLink>
                  </HeaderWithBack>

                  <FloatingTextField
                    id="courseName"
                    name="courseName"
                    label={intl.formatMessage(messages.courseName)}
                    value={courseTitles.courseName}
                    onChange={updateTitle}
                    fontSize={16}
                    required
                    error={errors.courseName}
                  />

                  <FloatingTextField
                    id="unitName"
                    name="unitName"
                    label={intl.formatMessage(messages.unitName)}
                    value={courseTitles.unitName}
                    onChange={updateTitle}
                    fontSize={16}
                    required
                    error={errors.unitName}
                  />

                  <FloatingTextField
                    id="lessonName"
                    name="lessonName"
                    label={intl.formatMessage(messages.lessonName)}
                    value={courseTitles.lessonName}
                    onChange={updateTitle}
                    fontSize={16}
                    required
                    error={errors.lessonName}
                  />

                  <FormRowWithButton>
                    <FormRowWithButtonInput>
                      <FloatingTextField
                        id="screenName"
                        name="screenName"
                        label={intl.formatMessage(messages.screenName)}
                        value={courseTitles.screenName}
                        onChange={updateTitle}
                        fontSize={16}
                        required
                        error={errors.screenName}
                        marginBottom={0}
                      />
                    </FormRowWithButtonInput>
                  </FormRowWithButton>

                  <CreateCourseButtonRow>
                    <CreateButton
                      type="button"
                      onClick={onCreateCourse}
                      disabled={!isFormValid || isCreatingCourse}
                      aria-busy={isCreatingCourse}
                    >
                      {intl.formatMessage(messages.createButton)}
                    </CreateButton>
                    {isCreatingCourse && (
                      <Spinner
                        xsmall
                        aria-label={intl.formatMessage(messages.creatingCourse)}
                      />
                    )}
                  </CreateCourseButtonRow>
                </CreateCourseForm>
              </FullHeightWrapperWithBackGroundColor>
            </FullScreenDialog>
          </FullScreenDialogWrapper>
        )
      }
    </>
  );
}

GuardRailsSelectorModal.propTypes = {
  projectId: PropTypes.string,
  onClose: PropTypes.func,
  onSelectOther: PropTypes.func,
  goToActivity: PropTypes.func,
};

export default GuardRailsSelectorModal;
